import React from 'react';
import { CaretLeft } from '@phosphor-icons/react';

// List group (handoff_app_design/README.md §4.3) — replaces tile grids. A rounded, bordered
// container of rows, each a full tap target, with a divider between rows (never after the last).
// Give the group a heading with <Section title="עוד באפליקציה"><ListGroup>...</ListGroup></Section>.
//
// A5: no colored square behind the row icon — that pattern reads as templated AI design. The one
// exception app-wide is the Chat card's solid accent tile, which isn't a list row.
//
//   <ListGroup>
//     <ListRow icon={<ChatCircle weight="bold" />} title="צ'אט עם המאמן" sub="נותרו 99 הודעות היום" onClick={...} />
//     ...
//   </ListGroup>
// `muted` — surface-2 fill instead of surface, for a de-emphasized group (e.g. the privileged
// "הרשאות מורחבות" section, which should read quieter than the screen's main content).
export function ListGroup({ className = '', muted = false, children }) {
  return (
    <div className={`rounded-[16px] ${muted ? 'bg-surface-2' : 'bg-surface'} border border-border overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

// Props:
//   icon      — leading icon element (Phosphor, bold weight, 22px), plain text-muted, no tile.
//   title     — row title (700 17px).
//   sub       — sub-line (500 13px, text-muted) — real, live data (e.g. "אתמול: טמפו 8 ק״מ, 4:36
//               לק״מ"), never a generic description.
//   trailing  — replaces the default chevron (e.g. a value or badge) when provided.
//   showCaret — set false to omit the trailing chevron entirely (default true when no `trailing`).
//   onClick / href — makes the whole row the tap target.
//   last      — true suppresses the row's own divider (ListGroup already omits it after the last
//               child automatically via :not(:last-child); pass this only for conditionally
//               rendered rows where the DOM-last child isn't the visually-last row).
export function ListRow({ icon, title, sub, trailing, showCaret = true, onClick, href, className = '' }) {
  const As = href ? 'a' : onClick ? 'button' : 'div';
  return (
    <As
      href={href}
      onClick={onClick}
      type={As === 'button' ? 'button' : undefined}
      className={`w-full flex items-center min-h-[64px] gap-[13px] px-4 py-[14px] text-start border-b border-divider last:border-b-0 ${(onClick || href) ? 'active:bg-pressed' : ''} ${className}`}
    >
      {icon && <span className="text-text-muted text-[22px] shrink-0 flex items-center">{icon}</span>}
      <span className="flex-1 min-w-0">
        <span className="block font-bold text-[17px] text-text truncate">{title}</span>
        {sub && <span className="block font-medium text-[13px] text-text-muted truncate mt-0.5">{sub}</span>}
      </span>
      {trailing !== undefined ? trailing : (showCaret && <CaretLeft size={18} className="text-text-muted shrink-0" />)}
    </As>
  );
}

export default ListGroup;
